import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API_BASE, getToken, type Entity } from "../api";
import FileDrop from "../components/FileDrop";

export default function NewExpense() {
  const nav = useNavigate();
  const [entities, setEntities] = useState<Entity[]>([]);
  const [entityId, setEntityId] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("EUR");
  const [category, setCategory] = useState("");
  const [label, setLabel] = useState("");
  const [notes, setNotes] = useState("");
  const [photo, setPhoto] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api<{ entities: Entity[] }>("/entities").then((r) => {
      setEntities(r.entities);
      if (r.entities[0]) setEntityId(r.entities[0].id);
    });
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    const cents = Math.round(parseFloat(amount.replace(",", ".")) * 100);
    if (!cents || isNaN(cents)) {
      setErr("Montant invalide");
      return;
    }
    setBusy(true);
    try {
      const r = await api<{ id: string }>("/expenses", {
        method: "POST",
        json: {
          entity_id: entityId,
          expense_date: new Date(date).getTime(),
          amount_cents: cents,
          currency,
          category: category.trim() || undefined,
          label: label.trim(),
          notes: notes.trim() || undefined,
        },
      });
      if (photo) {
        const buf = await photo.arrayBuffer();
        const res = await fetch(`${API_BASE}/expenses/${r.id}/file`, {
          method: "PUT",
          headers: {
            "Content-Type": photo.type || "image/jpeg",
            Authorization: `Bearer ${getToken()}`,
          },
          body: buf,
        });
        if (!res.ok) throw new Error("Échec téléversement justificatif");
      }
      nav(`/expenses/${r.id}`);
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h2>Nouvelle note de frais</h2>
      <form onSubmit={submit}>
        <label className="section-h">Entité</label>
        <select value={entityId} onChange={(e) => setEntityId(e.target.value)} required>
          {entities.map((e) => (<option key={e.id} value={e.id}>{e.name}</option>))}
        </select>

        <label className="section-h" style={{ marginTop: 14 }}>Date</label>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />

        <label className="section-h" style={{ marginTop: 14 }}>Montant</label>
        <div className="row" style={{ gap: 8 }}>
          <input
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0,00"
            required
            style={{ flex: 2 }}
          />
          <select value={currency} onChange={(e) => setCurrency(e.target.value)} style={{ flex: 1 }}>
            <option value="EUR">EUR</option>
            <option value="USD">USD</option>
            <option value="GBP">GBP</option>
            <option value="CHF">CHF</option>
          </select>
        </div>

        <label className="section-h" style={{ marginTop: 14 }}>Libellé</label>
        <input value={label} onChange={(e) => setLabel(e.target.value)} required maxLength={140} />

        <label className="section-h" style={{ marginTop: 14 }}>Catégorie (optionnel)</label>
        <input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Restaurant, Transport…" />

        <label className="section-h" style={{ marginTop: 14 }}>Notes (optionnel)</label>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />

        <label className="section-h" style={{ marginTop: 14 }}>
          Justificatif (photo, scan ou import)
        </label>
        <FileDrop
          accept="image/*,application/pdf"
          capture="environment"
          onPick={setPhoto}
          withPreview
          hint="Glisse-dépose une image, ou clique pour scanner avec l'appareil photo"
        />

        {err && <div className="error" style={{ marginTop: 10 }}>{err}</div>}

        <div style={{ height: 18 }} />
        <button className="btn btn-primary full" type="submit" disabled={busy || !label || !amount || !entityId}>
          {busy ? "Envoi…" : "Envoyer la note"}
        </button>
      </form>
    </>
  );
}
